/**
 * Merge research-API fields with campaign custom field definitions for entity detail views.
 */

export function isEmptyCustomFieldValue(value) {
  if (value === null || value === undefined) return true;
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === 'object') return Object.keys(value).length === 0;
  const s = String(value).trim();
  return s === '' || s === 'N/A';
}

/** Prefer table/pipeline values; never show "Researching" when we have displayable data. */
export function resolveDetailFieldValueAndLoading(field, entityCustomFields) {
  const id = String(field.id || field.field_id || '');
  const fromPipeline = id ? entityCustomFields?.[id] : undefined;
  const value = !isEmptyCustomFieldValue(fromPipeline) ? fromPipeline : field.value;
  const loading = Boolean(field.loading) && isEmptyCustomFieldValue(value);
  return { value, loading };
}

function objectIdTimestampMs(id) {
  if (!id || typeof id !== 'string' || id.length !== 24 || !/^[a-f0-9]{24}$/i.test(id)) {
    return 0;
  }
  try {
    return parseInt(id.substring(0, 8), 16) * 1000;
  } catch {
    return 0;
  }
}

function fieldSortKey(field, orderByDefIndex) {
  const id = String(field.id || field.field_id || '');
  const defOrder = orderByDefIndex.get(id);
  if (defOrder !== undefined) return { kind: 0, t: defOrder, id };
  const parsed = Date.parse(field.created_at || field.version_created || '');
  const t = Number.isFinite(parsed) && parsed > 0 ? parsed : objectIdTimestampMs(id);
  return { kind: 1, t, id };
}

/** Oldest-first: campaign column order when available, else ObjectId / created_at. */
export function sortDetailViewFieldsChronologically(fields, fieldDefinitions = []) {
  const orderByDefIndex = new Map();
  (fieldDefinitions || []).forEach((def, idx) => {
    const id = String(def.field_id || '');
    if (id) orderByDefIndex.set(id, idx);
  });

  return [...fields].sort((a, b) => {
    const ka = fieldSortKey(a, orderByDefIndex);
    const kb = fieldSortKey(b, orderByDefIndex);
    if (ka.kind !== kb.kind) return ka.kind - kb.kind;
    if (ka.t !== kb.t) return ka.t - kb.t;
    return ka.id.localeCompare(kb.id);
  });
}

/**
 * @param {object} params
 * @param {Array} params.apiFields - from GET .../fields (research DB)
 * @param {Array} params.fieldDefinitions - campaign field defs (sales_pipeline)
 * @param {Record<string, unknown>} params.entityCustomFields - entity.custom_fields map
 * @param {string[]} [params.excludeFieldIds] - e.g. notes / contacted columns
 */
export function mergeDetailViewFields({
  apiFields = [],
  fieldDefinitions = [],
  entityCustomFields = {},
  excludeFieldIds = [],
}) {
  const exclude = new Set(excludeFieldIds.filter(Boolean).map(String));
  const byId = new Map();

  for (const field of apiFields) {
    const id = String(field.id || field._id || field.field_id || '');
    if (!id || exclude.has(id)) continue;
    const { value, loading } = resolveDetailFieldValueAndLoading(field, entityCustomFields);
    byId.set(id, {
      ...field,
      id,
      _id: id,
      field_id: id,
      label: field.label || field.display_name || field.field_name || '',
      value,
      loading,
      version_created: field.version_created,
    });
  }

  for (const fieldDef of fieldDefinitions || []) {
    const id = String(fieldDef.field_id || '');
    if (!id || exclude.has(id)) continue;
    if (byId.has(id)) continue;

    const value = entityCustomFields?.[id];
    if (isEmptyCustomFieldValue(value)) continue;

    const loading =
      fieldDef.status === 'generating' && isEmptyCustomFieldValue(value);

    byId.set(id, {
      id,
      _id: id,
      field_id: id,
      field_name: fieldDef.field_name,
      display_name: fieldDef.display_name,
      label: fieldDef.display_name || fieldDef.field_name || id,
      description: fieldDef.description || '',
      config_id: fieldDef.config_id || null,
      source: fieldDef.source || null,
      status: fieldDef.status || 'completed',
      output_schema: fieldDef.output_schema,
      value,
      loading,
      created_at: fieldDef.created_at,
    });
  }

  return sortDetailViewFieldsChronologically(
    Array.from(byId.values()),
    fieldDefinitions,
  );
}
